// src/Components/RecipeDetails.js
// Secure, production-hardened refactor. Keeps markup and classes identical so existing CSS works unchanged.
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './randi.css';

// Create a small axios instance with safe defaults for this component
const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || undefined,
  withCredentials: true,
  timeout: 10000,
  headers: {
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Simple text escaper to avoid accidental HTML injection in places where innerHTML might be used later
const escapeText = (str = '') =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Validate recipe id (allow alphanumeric, hyphen, underscore) to avoid unexpected characters
const isValidRecipeId = (id) => typeof id === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(id);

const splitAndNormalize = (text = '', delimiterRegex, maxItems = 500) => {
  if (!text || typeof text !== 'string') return [];
  // Split using provided regex, trim, filter empties, and limit items for safety
  return text
    .split(delimiterRegex)
    .map(s => s.trim())
    .filter(Boolean)
    .slice(0, maxItems);
};

const RecipeDetails = () => {
  const { recipeId } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isValidRecipeId(recipeId)) {
      setError('Invalid recipe identifier.');
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const fetchRecipe = async () => {
      setLoading(true);
      setError(null);
      try {
        // encode recipeId to avoid any URL injection
        const safeId = encodeURIComponent(recipeId);
        const resp = await api.get(`/ttc/api/recipe/${safeId}`, { signal: controller.signal });
        const data = resp?.data ?? null;
        if (!data || typeof data !== 'object' || !data.title) {
          setError('Recipe not found.');
          setRecipe(null);
        } else {
          // Normalize and keep only expected fields to reduce attack surface
          const normalized = {
            id: String(data.id ?? ''),
            title: String(data.title ?? '').slice(0, 300),
            ingredients: String(data.ingredients ?? ''),
            instructions: String(data.instructions ?? ''),
          };
          setRecipe(normalized);
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          // request cancelled, ignore
        } else {
          console.error('Error fetching recipe details:', err);
          setError('Error fetching recipe details. Please try again later.');
          setRecipe(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();
    return () => controller.abort();
  }, [recipeId]);

  const ingredients = useMemo(() => {
    // Ingredients are stored as pipe-separated values
    return splitAndNormalize(recipe?.ingredients, /\|/g, 300);
  }, [recipe]);

  const instructions = useMemo(() => {
    // Split on sentence endings (period/question/exclamation) but keep reasonable limit
    return splitAndNormalize(recipe?.instructions, /(?<=[.!?])\s+/g, 500);
  }, [recipe]);

  if (loading) {
    return <div>Loading recipe details...</div>;
  }

  if (error) {
    return <div>{escapeText(error)}</div>;
  }

  if (!recipe || !recipe.title) {
    return <div>Recipe not available.</div>;
  }

  return (
    <div id="outer">
      <div id="inner_remaining">
        <div className="content">
          <div className="recp_name">
            <b>
              <p>🍽️ {escapeText(recipe.title)}</p>
              <p><i>Ingredients</i></p>
            </b>
            <div className="ingredients-container">
              <table id="ing1">
                {ingredients.map((ingredient, index) => (
                  index % 3 === 0 && (
                    <tr key={index}>
                      {ingredients.slice(index, index + 3).map((ing, i) => (
                        <td key={i}>{escapeText(ing)}</td>
                      ))}
                    </tr>
                  )
                ))}
              </table>
              <ul id="ing2">
                {ingredients.map((ingredient, index) => (
                  <li key={index} style={{ listStyle: 'none', padding: 0 }}>✅ {escapeText(ingredient)}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="instructions">
          <b><i>Instructions</i></b>
          <ul>
            {instructions.map((instruction, index) => (
              <li key={index}><b>🥣 {escapeText(instruction.charAt(0).toUpperCase() + instruction.slice(1))}</b></li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RecipeDetails;
